'use client';

import * as React from 'react';
import { ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/primitives/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/primitives/sheet';
import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { trackTicketHandoffContinue } from '@/lib/analytics';

// ---------------------------------------------------------------------------
// Responsive hook — matches the spec's 768px split for bottom-sheet vs modal.
// ---------------------------------------------------------------------------

/** Tablet breakpoint — mobile (<768px) gets the bottom sheet; tablet+ gets the centered modal. */
const TABLET_BREAKPOINT = 768;

// Default to `false` on both SSR and first client render so markup matches;
// the effect below switches to the real viewport state after hydration. The
// dialog is only rendered when `open=true` (user-driven), so the brief
// mobile-default render on tablet+ never reaches the screen in practice.
function useIsTabletOrUp(): boolean {
  const [isTablet, setIsTablet] = React.useState<boolean>(false);

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${TABLET_BREAKPOINT}px)`);
    const onChange = (): void => setIsTablet(mql.matches);
    setIsTablet(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isTablet;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TicketHandoffDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  partnerName: string;
  partnerLogo?: React.ReactNode;
  venueName: string | null;
  finalPrice: number | null;
  currency: string;
  affiliateUrl: string;
  isLoading?: boolean;
  /** Force a render mode regardless of viewport — Storybook / tests only. */
  mode?: 'sheet' | 'dialog';
  className?: string;
  /** Optional seam for analytics + navigation (defaults apply trackTicketHandoffContinue + window.open). */
  onContinue?: () => void;
}

export interface TicketHandoffDialogProps extends Omit<
  TicketHandoffDialogViewProps,
  'onContinue' | 'mode'
> {}

// ---------------------------------------------------------------------------
// Price formatting
// ---------------------------------------------------------------------------

function formatFinalPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

// ---------------------------------------------------------------------------
// Shared content — rendered inside both Sheet and Dialog variants.
// ---------------------------------------------------------------------------

interface HandoffBodyProps {
  partnerName: string;
  partnerLogo?: React.ReactNode;
  venueName: string | null;
  finalPrice: number | null;
  currency: string;
  isLoading: boolean;
}

function PartnerLockup({
  partnerName,
  partnerLogo,
}: Pick<HandoffBodyProps, 'partnerName' | 'partnerLogo'>): React.JSX.Element {
  return (
    <div
      data-testid="handoff-partner-lockup"
      className="flex items-center justify-center gap-3 font-display text-lg font-bold tracking-wide"
      aria-label={`LOCI to ${partnerName}`}
    >
      <span className="text-foreground">LOCI</span>
      <ArrowRight className="size-4 text-muted-foreground" aria-hidden="true" />
      {partnerLogo ? (
        <span data-testid="handoff-partner-logo" className="inline-flex items-center">
          {partnerLogo}
        </span>
      ) : (
        <span data-testid="handoff-partner-name" className="text-foreground">
          {partnerName}
        </span>
      )}
    </div>
  );
}

function HandoffBody({
  partnerName,
  partnerLogo,
  venueName,
  finalPrice,
  currency,
  isLoading,
}: HandoffBodyProps): React.JSX.Element {
  if (isLoading) {
    return (
      <div data-testid="handoff-loading" className="flex flex-col items-center gap-5 py-2">
        <div className="h-6 w-48 animate-pulse rounded-md bg-muted" aria-hidden="true" />
        <div className="h-8 w-56 animate-pulse rounded-md bg-muted" aria-hidden="true" />
        <div className="h-5 w-32 animate-pulse rounded-full bg-muted" aria-hidden="true" />
        <div className="h-5 w-40 animate-pulse rounded-md bg-muted" aria-hidden="true" />
        <span className="sr-only">Loading ticket handoff details</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <PartnerLockup partnerName={partnerName} partnerLogo={partnerLogo} />
      {venueName && (
        <p data-testid="handoff-subline" className="text-sm text-muted-foreground">
          Our Official Ticket Partner for {venueName}
        </p>
      )}
      <span
        data-testid="handoff-trust-badge"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
          'bg-loci-success text-white',
        )}
      >
        <span aria-hidden="true">✓</span>
        Official ticket partner · trusted
      </span>
      {finalPrice !== null && (
        <div className="flex flex-col items-center gap-1">
          <span
            data-testid="handoff-price"
            className="font-mono text-2xl font-semibold tabular-nums text-foreground"
          >
            {formatFinalPrice(finalPrice, currency)}
          </span>
          <span className="text-xs text-muted-foreground">Incl. fees · no markup from LOCI</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View — presentational, used by Storybook and tests.
// ---------------------------------------------------------------------------

/**
 * @deprecated FRE-544-era organism — superseded by the `AtAGlanceCard` outbound
 *   flow in the M2 event detail template.
 *   Slated for physical removal in a post-launch cleanup PR.
 *   See Linear WEB-196.
 */
export function TicketHandoffDialogView({
  open,
  onOpenChange,
  eventId,
  partnerName,
  partnerLogo,
  venueName,
  finalPrice,
  currency,
  affiliateUrl,
  isLoading = false,
  mode,
  className,
  onContinue,
}: TicketHandoffDialogViewProps): React.JSX.Element {
  const autoMode = useIsTabletOrUp() ? 'dialog' : 'sheet';
  const resolvedMode = mode ?? autoMode;

  const handleContinue = React.useCallback((): void => {
    if (isLoading) return;
    if (onContinue) {
      onContinue();
      return;
    }
    trackTicketHandoffContinue(eventId, partnerName, finalPrice);
    window.open(affiliateUrl, '_blank', 'noopener');
    onOpenChange(false);
  }, [affiliateUrl, eventId, finalPrice, isLoading, onContinue, onOpenChange, partnerName]);

  const handleReturn = React.useCallback((): void => {
    onOpenChange(false);
  }, [onOpenChange]);

  const titleText = `Connecting to ${partnerName}`;
  const descriptionText = venueName
    ? `Review fees and continue to ${partnerName} to purchase tickets for ${venueName}.`
    : `Review fees and continue to ${partnerName} to purchase tickets.`;

  const continueLabel = `Continue to ${partnerName}`;
  const returnLabel = 'Return to LOCI';

  const actions = (
    <div className="flex w-full flex-col gap-2">
      <Button
        variant="cta"
        block
        onClick={handleContinue}
        disabled={isLoading}
        data-testid="handoff-continue-button"
        aria-label={`${continueLabel}. Opens in new tab`}
      >
        {continueLabel}
      </Button>
      <Button variant="ghost" block onClick={handleReturn} data-testid="handoff-return-button">
        {returnLabel}
      </Button>
    </div>
  );

  if (resolvedMode === 'sheet') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid="handoff-sheet-content"
          title={null}
          overlayClassName="bg-black/70 backdrop-blur-[8px]"
          className={cn(
            'rounded-t-2xl px-4 pt-4',
            'pb-[calc(1rem+env(safe-area-inset-bottom))]',
            className,
          )}
        >
          {/* Drag handle */}
          <div
            aria-hidden="true"
            className="mx-auto h-1.5 w-10 rounded-full bg-muted-foreground/30"
            data-testid="handoff-drag-handle"
          />
          <SheetHeader className="items-center pb-0 text-center">
            <SheetTitle className="font-display text-h4 font-bold leading-tight">
              {titleText}
            </SheetTitle>
            <SheetDescription className="sr-only">{descriptionText}</SheetDescription>
          </SheetHeader>
          <HandoffBody
            partnerName={partnerName}
            partnerLogo={partnerLogo}
            venueName={venueName}
            finalPrice={finalPrice}
            currency={currency}
            isLoading={isLoading}
          />
          <SheetFooter className="mt-auto p-0 pt-2">{actions}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="handoff-dialog-content"
        className={cn('max-w-[480px] bg-background', className)}
        overlayClassName="bg-black/70 backdrop-blur-[8px]"
      >
        <DialogHeader className="items-center text-center">
          <DialogTitle className="font-display text-h4 font-bold leading-tight">
            {titleText}
          </DialogTitle>
          <DialogDescription className="sr-only">{descriptionText}</DialogDescription>
        </DialogHeader>
        <HandoffBody
          partnerName={partnerName}
          partnerLogo={partnerLogo}
          venueName={venueName}
          finalPrice={finalPrice}
          currency={currency}
          isLoading={isLoading}
        />
        <DialogFooter className="flex-col sm:flex-col">{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Connected — convenience wrapper that defaults the analytics/navigation seam.
// ---------------------------------------------------------------------------

/**
 * @deprecated FRE-544-era organism — superseded by the `AtAGlanceCard` outbound
 *   flow in the M2 event detail template.
 *   Slated for physical removal in a post-launch cleanup PR.
 *   See Linear WEB-196.
 */
export function TicketHandoffDialog(props: TicketHandoffDialogProps): React.JSX.Element {
  return <TicketHandoffDialogView {...props} />;
}
